import React, { useEffect, useMemo, useState } from 'react';
import { Eye, EyeOff, ExternalLink, Save } from 'lucide-react';
import api from '../../utils/api';
import { invalidateSiteContent } from '../../hooks/useSiteContent';

// Visual preview editor for marketing copy on the public site. Each page sub-tab
// renders the sections that page is composed of, laid out roughly the way they
// appear to visitors so the tutor can edit copy in context. Writes go through
// the existing PUT /api/site-content/:key endpoint.

interface SiteContentRow {
  id: string;
  key: string;
  label: string;
  value: string;
  isVisible: boolean;
  section: string;
  sortOrder: number;
}

interface Edits {
  [key: string]: { value: string; isVisible: boolean };
}

type PageKey = 'home' | 'about' | 'subjects' | 'oxbridge' | 'contact' | 'footer';

interface PageDef {
  key: PageKey;
  label: string;
  sectionGroups: string[];
}

const PAGES: PageDef[] = [
  {
    key: 'home',
    label: 'Home',
    sectionGroups: [
      'Home - Hero',
      'Home - Stats',
      'Home - About',
      'Home - How It Works',
      'Home - Testimonials',
      'Home - Outcomes',
      'Home - FAQ',
    ],
  },
  { key: 'about', label: 'About', sectionGroups: ['About Page'] },
  { key: 'subjects', label: 'Subjects and rates', sectionGroups: ['Subjects Page', 'Rates Page'] },
  { key: 'oxbridge', label: 'Oxbridge', sectionGroups: ['Oxbridge Page'] },
  { key: 'contact', label: 'Contact', sectionGroups: ['Contact Page'] },
  { key: 'footer', label: 'Footer', sectionGroups: ['Footer'] },
];

// Shared styling tokens for preview cards. Each card has a thin top rule and
// generous vertical padding so the visual separation between sections is clear.
const PREVIEW_CARD =
  'border-t-2 border-ochre/70 bg-paper rounded-card px-6 md:px-10 py-12 md:py-14 mb-10';

interface FieldProps {
  fieldKey: string;
  row: SiteContentRow | undefined;
  edit: { value: string; isVisible: boolean } | undefined;
  onChange: (key: string, patch: Partial<{ value: string; isVisible: boolean }>) => void;
  /** Styling that approximates how the string appears on the public site. */
  previewClass?: string;
  /** Force textarea instead of input (multiline copy). */
  multiline?: boolean;
  /** Hint rows for textarea height. */
  rows?: number;
  /** Hint text shown if the row is missing from the DB. */
  missingHint?: string;
}

// Inline editable field — the textarea/input is itself the visual preview. The
// small monospace label beneath tells the tutor which site_content key it maps
// to so they can still cross-reference the old flat list.
const Field: React.FC<FieldProps> = ({
  fieldKey,
  row,
  edit,
  onChange,
  previewClass = 'font-sans text-[15px] text-ink',
  multiline = false,
  rows,
  missingHint,
}) => {
  const fallback = row?.value ?? '';
  const value = edit?.value ?? fallback;
  const visible = edit?.isVisible ?? row?.isVisible ?? true;
  const isMissing = !row;

  const sharedClass =
    `w-full bg-transparent border border-transparent hover:border-rule focus:border-ochre ` +
    `focus:outline-none focus:ring-0 rounded-sm px-2 py-1 transition-colors resize-y ` +
    `placeholder:text-muted/60 ${previewClass}`;

  return (
    <div className="group">
      {multiline ? (
        <textarea
          value={value}
          rows={rows ?? 3}
          placeholder={missingHint}
          onChange={e => onChange(fieldKey, { value: e.target.value })}
          className={sharedClass}
        />
      ) : (
        <input
          type="text"
          value={value}
          placeholder={missingHint}
          onChange={e => onChange(fieldKey, { value: e.target.value })}
          className={sharedClass}
        />
      )}
      <div className="mt-1 flex items-center justify-between gap-3">
        <code className="text-[11px] font-mono text-muted">{fieldKey}</code>
        <div className="flex items-center gap-3 text-[11px]">
          {isMissing && (
            <span className="text-ochre">not in DB — will be created on save</span>
          )}
          <button
            type="button"
            onClick={() => onChange(fieldKey, { isVisible: !visible })}
            className={`inline-flex items-center gap-1 text-muted hover:text-ochre transition-colors ${
              visible ? '' : 'text-ochre'
            }`}
            title={visible ? 'Visible on site — click to hide' : 'Hidden — click to show'}
          >
            {visible ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
            <span>{visible ? 'visible' : 'hidden'}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

interface SectionCardProps {
  title: string;
  caption?: string;
  rows: SiteContentRow[];
  edits: Edits;
  onChange: (key: string, patch: Partial<{ value: string; isVisible: boolean }>) => void;
  onSave: (keys: string[]) => void;
  saving: boolean;
  /** The actual layout mirroring the public section. Receives a helper that
   *  resolves a key to the SiteContentRow (undefined if seed row missing). */
  children: (byKey: (k: string) => SiteContentRow | undefined) => React.ReactNode;
}

const SectionCard: React.FC<SectionCardProps> = ({
  title,
  caption,
  rows,
  edits,
  onChange,
  onSave,
  saving,
  children,
}) => {
  const byKey = (k: string) => rows.find(r => r.key === k);

  // Only the keys that this section actually renders count as "dirty" for the
  // per-section save button. We infer that from the edits map — if a field has
  // been touched we'll save it. (Untouched fields send the current value back
  // which is a no-op upsert; harmless.)
  const sectionKeys = rows.map(r => r.key);
  const dirty = sectionKeys.some(k => {
    const e = edits[k];
    const r = byKey(k);
    if (!e || !r) return false;
    return e.value !== r.value || e.isVisible !== r.isVisible;
  });

  return (
    <div className={PREVIEW_CARD}>
      <div className="flex items-start justify-between gap-6 mb-6">
        <div>
          <p className="eyebrow">{title}</p>
          {caption && <p className="mt-2 text-[13px] text-muted max-w-xl">{caption}</p>}
        </div>
        <button
          type="button"
          onClick={() => onSave(sectionKeys)}
          disabled={saving || !dirty}
          className="btn btn-primary inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Save className="w-4 h-4" />
          {saving ? 'Saving…' : dirty ? 'Save section' : 'Saved'}
        </button>
      </div>
      <div className="bg-cream/40 border border-rule rounded-card p-6 md:p-8">
        {children(byKey)}
      </div>
    </div>
  );
};

interface Props {
  sectionGroups?: string[];
}

// Main component. Manages load + edits + save, and routes the relevant rows
// down to per-page layouts that mirror the public site structure.
const PagePreviewEditor: React.FC<Props> = () => {
  const [items, setItems] = useState<SiteContentRow[]>([]);
  const [edits, setEdits] = useState<Edits>({});
  const [loading, setLoading] = useState(true);
  const [savingKeys, setSavingKeys] = useState<Record<string, boolean>>({});
  const [activePage, setActivePage] = useState<PageKey>('home');

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/site-content');
      const rows: SiteContentRow[] = Array.isArray(res.data) ? res.data : [];
      setItems(rows);
      const fresh: Edits = {};
      rows.forEach(r => { fresh[r.key] = { value: r.value, isVisible: r.isVisible }; });
      setEdits(fresh);
    } catch (err) {
      console.error('Failed to load site content:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onChange = (key: string, patch: Partial<{ value: string; isVisible: boolean }>) => {
    setEdits(prev => {
      const current = prev[key] ?? { value: '', isVisible: true };
      return { ...prev, [key]: { ...current, ...patch } };
    });
  };

  const saveKeys = async (keys: string[]) => {
    // Only save keys that actually changed — avoid hammering the endpoint with
    // no-op upserts on untouched fields.
    const dirtyKeys = keys.filter(k => {
      const e = edits[k];
      const r = items.find(i => i.key === k);
      if (!e) return false;
      if (!r) return true; // new row that wasn't seeded
      return e.value !== r.value || e.isVisible !== r.isVisible;
    });
    if (dirtyKeys.length === 0) return;

    const marker: Record<string, boolean> = {};
    dirtyKeys.forEach(k => { marker[k] = true; });
    setSavingKeys(prev => ({ ...prev, ...marker }));

    try {
      await Promise.all(
        dirtyKeys.map(k =>
          api.put(`/site-content/${k}`, {
            value: edits[k].value,
            isVisible: edits[k].isVisible,
          }),
        ),
      );
      invalidateSiteContent();
      await load();
    } catch (err: any) {
      window.alert(err?.response?.data?.message || 'Failed to save section');
    } finally {
      const unmark: Record<string, boolean> = {};
      dirtyKeys.forEach(k => { unmark[k] = false; });
      setSavingKeys(prev => ({ ...prev, ...unmark }));
    }
  };

  const rowsForGroups = (groups: string[]): SiteContentRow[] =>
    items.filter(r => groups.includes(r.section));

  const activeDef = useMemo(() => PAGES.find(p => p.key === activePage)!, [activePage]);
  const pageRows = useMemo(() => rowsForGroups(activeDef.sectionGroups), [items, activeDef]);

  if (loading) {
    return <p className="text-muted">Loading site content…</p>;
  }

  return (
    <div>
      {/* Sub-tabs — one per public page */}
      <div className="flex flex-wrap items-center gap-2 border-b border-rule pb-3 mb-8">
        {PAGES.map(p => (
          <button
            key={p.key}
            type="button"
            onClick={() => setActivePage(p.key)}
            className={`px-4 py-2 text-[14px] rounded-full border transition-colors ${
              activePage === p.key
                ? 'bg-ochre text-white border-ochre'
                : 'bg-transparent text-ink border-rule hover:border-ochre hover:text-ochre'
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      <div className="mb-6 flex items-center gap-3 text-[13px] text-muted">
        <span>
          Editing <span className="text-ink font-medium">{activeDef.label}</span> — click any text to edit, then hit "Save section".
        </span>
      </div>

      {activePage === 'home' && (
        <HomePreview rows={pageRows} edits={edits} onChange={onChange} onSave={saveKeys} savingKeys={savingKeys} />
      )}
      {activePage === 'about' && (
        <AboutPreview rows={pageRows} edits={edits} onChange={onChange} onSave={saveKeys} savingKeys={savingKeys} />
      )}
      {activePage === 'subjects' && (
        <SubjectsPreview rows={pageRows} edits={edits} onChange={onChange} onSave={saveKeys} savingKeys={savingKeys} />
      )}
      {activePage === 'oxbridge' && (
        <OxbridgePreview rows={pageRows} edits={edits} onChange={onChange} onSave={saveKeys} savingKeys={savingKeys} />
      )}
      {activePage === 'contact' && (
        <ContactPreview rows={pageRows} edits={edits} onChange={onChange} onSave={saveKeys} savingKeys={savingKeys} />
      )}
      {activePage === 'footer' && (
        <FooterPreview rows={pageRows} edits={edits} onChange={onChange} onSave={saveKeys} savingKeys={savingKeys} />
      )}
    </div>
  );
};

// ─── Per-page layouts ────────────────────────────────────────────────────────
// Each renders the sections belonging to that page using the same visual
// conventions as the public site (fonts, weights, colours, approximate sizes).
// The actual content is the <textarea>/<input> inside Field, so what the tutor
// types is exactly what ships.

interface PreviewProps {
  rows: SiteContentRow[];
  edits: Edits;
  onChange: (key: string, patch: Partial<{ value: string; isVisible: boolean }>) => void;
  onSave: (keys: string[]) => void;
  savingKeys: Record<string, boolean>;
}

const anySaving = (keys: string[], savingKeys: Record<string, boolean>) =>
  keys.some(k => savingKeys[k]);

// Rows live in the selected page's section group but may span multiple
// sections — this helper scopes to a single section name.
const rowsIn = (rows: SiteContentRow[], section: string) =>
  rows.filter(r => r.section === section);

const HomePreview: React.FC<PreviewProps> = ({ rows, edits, onChange, onSave, savingKeys }) => {
  const heroRows = rowsIn(rows, 'Home - Hero');
  const statsRows = rowsIn(rows, 'Home - Stats');
  const aboutRows = rowsIn(rows, 'Home - About');
  const howRows = rowsIn(rows, 'Home - How It Works');
  const testiRows = rowsIn(rows, 'Home - Testimonials');
  const outcomesRows = rowsIn(rows, 'Home - Outcomes');
  const faqRows = rowsIn(rows, 'Home - FAQ');

  return (
    <>
      {/* Hero */}
      <SectionCard
        title="Home — Hero"
        caption="The first fold of the landing page: eyebrow label, big headline, subhead, CTAs and a proof row."
        rows={heroRows}
        edits={edits}
        onChange={onChange}
        onSave={onSave}
        saving={anySaving(heroRows.map(r => r.key), savingKeys)}
      >
        {() => (
          <>
            <Field
              fieldKey="hero_eyebrow"
              row={heroRows.find(r => r.key === 'hero_eyebrow')}
              edit={edits.hero_eyebrow}
              onChange={onChange}
              previewClass="eyebrow"
            />
            <div className="mt-5">
              <Field
                fieldKey="hero_h1"
                row={heroRows.find(r => r.key === 'hero_h1')}
                edit={edits.hero_h1}
                onChange={onChange}
                previewClass="font-serif text-[clamp(2rem,4.5vw,3.5rem)] leading-[1.05] tracking-tight text-ink"
                multiline
                rows={2}
              />
            </div>
            <div className="mt-5 max-w-[62ch]">
              <Field
                fieldKey="hero_sub"
                row={heroRows.find(r => r.key === 'hero_sub')}
                edit={edits.hero_sub}
                onChange={onChange}
                previewClass="font-sans text-[17px] leading-[1.55] text-ink/90"
                multiline
                rows={3}
              />
            </div>
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <div className="inline-block bg-ochre text-white px-5 py-2.5 rounded-sm">
                <Field
                  fieldKey="hero_cta_primary"
                  row={heroRows.find(r => r.key === 'hero_cta_primary')}
                  edit={edits.hero_cta_primary}
                  onChange={onChange}
                  previewClass="font-sans text-[15px] text-white placeholder:text-white/60"
                />
              </div>
              <div className="inline-block border border-ink text-ink px-5 py-2.5 rounded-sm">
                <Field
                  fieldKey="hero_cta_secondary"
                  row={heroRows.find(r => r.key === 'hero_cta_secondary')}
                  edit={edits.hero_cta_secondary}
                  onChange={onChange}
                  previewClass="font-sans text-[15px] text-ink"
                />
              </div>
            </div>
            <div className="mt-8 max-w-[70ch]">
              <Field
                fieldKey="hero_proof_row"
                row={heroRows.find(r => r.key === 'hero_proof_row')}
                edit={edits.hero_proof_row}
                onChange={onChange}
                previewClass="font-sans text-[13px] leading-relaxed text-muted"
                multiline
                rows={2}
              />
            </div>
          </>
        )}
      </SectionCard>

      {/* Stats */}
      <SectionCard
        title="Home — Stats"
        caption="Four big ochre figures with a short caption underneath each."
        rows={statsRows}
        edits={edits}
        onChange={onChange}
        onSave={onSave}
        saving={anySaving(statsRows.map(r => r.key), savingKeys)}
      >
        {() => (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-10">
            {[
              ['stat_gcse', 'stat_gcse_sub'],
              ['stat_oxford', 'stat_oxford_sub'],
              ['stat_debate', 'stat_debate_sub'],
              ['stat_dbs', 'stat_dbs_sub'],
            ].map(([figKey, subKey]) => (
              <div key={figKey}>
                <Field
                  fieldKey={figKey}
                  row={statsRows.find(r => r.key === figKey)}
                  edit={edits[figKey]}
                  onChange={onChange}
                  previewClass="font-serif text-ochre text-[clamp(2.5rem,4vw,3.5rem)] leading-none tracking-tight"
                />
                <div className="mt-2">
                  <Field
                    fieldKey={subKey}
                    row={statsRows.find(r => r.key === subKey)}
                    edit={edits[subKey]}
                    onChange={onChange}
                    previewClass="font-sans text-[13px] leading-snug text-muted"
                    multiline
                    rows={2}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </SectionCard>

      {/* About */}
      <SectionCard
        title="Home — About (short)"
        caption="Short intro that sits above the fold. Full version lives on the About page."
        rows={aboutRows}
        edits={edits}
        onChange={onChange}
        onSave={onSave}
        saving={anySaving(aboutRows.map(r => r.key), savingKeys)}
      >
        {() => (
          <div className="max-w-[70ch]">
            <Field
              fieldKey="home_about_h"
              row={aboutRows.find(r => r.key === 'home_about_h')}
              edit={edits.home_about_h}
              onChange={onChange}
              previewClass="font-serif text-[clamp(1.75rem,3.5vw,2.5rem)] leading-[1.1] text-ink"
            />
            <div className="mt-5">
              <Field
                fieldKey="home_about_body"
                row={aboutRows.find(r => r.key === 'home_about_body')}
                edit={edits.home_about_body}
                onChange={onChange}
                previewClass="font-sans text-[17px] leading-[1.55] text-ink/90"
                multiline
                rows={4}
              />
            </div>
            <div className="mt-5">
              <Field
                fieldKey="home_about_cta"
                row={aboutRows.find(r => r.key === 'home_about_cta')}
                edit={edits.home_about_cta}
                onChange={onChange}
                previewClass="font-sans text-[15px] text-ochre underline underline-offset-4"
              />
            </div>
          </div>
        )}
      </SectionCard>

      {/* How It Works */}
      <SectionCard
        title="Home — How It Works"
        caption="Three horizontal blocks, each with an ochre 01/02/03 numeral, heading, body."
        rows={howRows}
        edits={edits}
        onChange={onChange}
        onSave={onSave}
        saving={anySaving(howRows.map(r => r.key), savingKeys)}
      >
        {() => (
          <>
            <Field
              fieldKey="how_heading"
              row={howRows.find(r => r.key === 'how_heading')}
              edit={edits.how_heading}
              onChange={onChange}
              previewClass="font-serif text-[clamp(1.75rem,3.5vw,2.5rem)] leading-[1.1] text-ink"
            />
            <div className="mt-8 grid md:grid-cols-3 gap-8">
              {[
                ['01', 'how_step1_h', 'how_step1_body'],
                ['02', 'how_step2_h', 'how_step2_body'],
                ['03', 'how_step3_h', 'how_step3_body'],
              ].map(([num, hKey, bKey]) => (
                <div key={num}>
                  <div className="font-serif text-ochre text-[28px] leading-none mb-3">{num}</div>
                  <div className="border-t border-rule pt-4">
                    <Field
                      fieldKey={hKey}
                      row={howRows.find(r => r.key === hKey)}
                      edit={edits[hKey]}
                      onChange={onChange}
                      previewClass="font-serif text-[20px] text-ink"
                    />
                    <div className="mt-3">
                      <Field
                        fieldKey={bKey}
                        row={howRows.find(r => r.key === bKey)}
                        edit={edits[bKey]}
                        onChange={onChange}
                        previewClass="font-sans text-[15px] leading-relaxed text-ink/90"
                        multiline
                        rows={4}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </SectionCard>

      {/* Testimonials — heading only */}
      <SectionCard
        title="Home — Testimonials heading"
        caption="Heading only. The actual quotes are managed in the Testimonials tab."
        rows={testiRows}
        edits={edits}
        onChange={onChange}
        onSave={onSave}
        saving={anySaving(testiRows.map(r => r.key), savingKeys)}
      >
        {() => (
          <>
            <Field
              fieldKey="testimonials_heading"
              row={testiRows.find(r => r.key === 'testimonials_heading')}
              edit={edits.testimonials_heading}
              onChange={onChange}
              previewClass="font-serif text-[clamp(1.75rem,3.5vw,2.5rem)] leading-[1.1] text-ink"
            />
            <p className="mt-4 text-[13px] text-muted inline-flex items-center gap-1">
              Quotes live in the <strong className="text-ink">Testimonials</strong> admin tab
              <ExternalLink className="w-3 h-3" />
            </p>
          </>
        )}
      </SectionCard>

      {/* Outcomes — heading only */}
      <SectionCard
        title="Home — Outcomes heading"
        caption="Heading only. The actual results rows are managed in the Outcomes tab."
        rows={outcomesRows}
        edits={edits}
        onChange={onChange}
        onSave={onSave}
        saving={anySaving(outcomesRows.map(r => r.key), savingKeys)}
      >
        {() => (
          <>
            <Field
              fieldKey="outcomes_heading"
              row={outcomesRows.find(r => r.key === 'outcomes_heading')}
              edit={edits.outcomes_heading}
              onChange={onChange}
              previewClass="font-serif text-[clamp(1.75rem,3.5vw,2.5rem)] leading-[1.1] text-ink"
            />
            <p className="mt-4 text-[13px] text-muted inline-flex items-center gap-1">
              Result rows live in the <strong className="text-ink">Outcomes</strong> admin tab
              <ExternalLink className="w-3 h-3" />
            </p>
          </>
        )}
      </SectionCard>

      {/* FAQ — heading only */}
      <SectionCard
        title="Home — FAQ heading"
        caption="Heading only. The Q&A rows are managed in the FAQ tab."
        rows={faqRows}
        edits={edits}
        onChange={onChange}
        onSave={onSave}
        saving={anySaving(faqRows.map(r => r.key), savingKeys)}
      >
        {() => (
          <>
            <Field
              fieldKey="faq_heading"
              row={faqRows.find(r => r.key === 'faq_heading')}
              edit={edits.faq_heading}
              onChange={onChange}
              previewClass="font-serif text-[clamp(1.75rem,3.5vw,2.5rem)] leading-[1.1] text-ink"
            />
            <p className="mt-4 text-[13px] text-muted inline-flex items-center gap-1">
              FAQ questions live in the <strong className="text-ink">FAQ</strong> admin tab
              <ExternalLink className="w-3 h-3" />
            </p>
          </>
        )}
      </SectionCard>
    </>
  );
};

const AboutPreview: React.FC<PreviewProps> = ({ rows, edits, onChange, onSave, savingKeys }) => {
  const groupA = rows.filter(r => ['about_p1', 'about_p2', 'about_p3', 'about_p4'].includes(r.key));
  const groupB = rows.filter(r => ['about_safeguarding_h', 'about_safeguarding_body'].includes(r.key));
  return (
    <>
      <SectionCard
        title="About — Paragraphs"
        caption="The four running-body paragraphs at the top of the About page."
        rows={groupA}
        edits={edits}
        onChange={onChange}
        onSave={onSave}
        saving={anySaving(groupA.map(r => r.key), savingKeys)}
      >
        {() => (
          <div className="max-w-[72ch] space-y-5">
            {['about_p1', 'about_p2', 'about_p3', 'about_p4'].map(k => (
              <Field
                key={k}
                fieldKey={k}
                row={groupA.find(r => r.key === k)}
                edit={edits[k]}
                onChange={onChange}
                previewClass="font-sans text-[17px] leading-[1.55] text-ink/90"
                multiline
                rows={4}
              />
            ))}
          </div>
        )}
      </SectionCard>

      <SectionCard
        title="About — Safeguarding"
        caption="The safeguarding card near the bottom of the About page."
        rows={groupB}
        edits={edits}
        onChange={onChange}
        onSave={onSave}
        saving={anySaving(groupB.map(r => r.key), savingKeys)}
      >
        {() => (
          <div className="max-w-[72ch]">
            <Field
              fieldKey="about_safeguarding_h"
              row={groupB.find(r => r.key === 'about_safeguarding_h')}
              edit={edits.about_safeguarding_h}
              onChange={onChange}
              previewClass="font-serif text-[clamp(1.75rem,3.5vw,2.5rem)] leading-[1.1] text-ink"
            />
            <div className="mt-4">
              <Field
                fieldKey="about_safeguarding_body"
                row={groupB.find(r => r.key === 'about_safeguarding_body')}
                edit={edits.about_safeguarding_body}
                onChange={onChange}
                previewClass="font-sans text-[17px] leading-[1.55] text-ink/90"
                multiline
                rows={4}
              />
            </div>
          </div>
        )}
      </SectionCard>
    </>
  );
};

const SubjectsPreview: React.FC<PreviewProps> = ({ rows, edits, onChange, onSave, savingKeys }) => {
  const headingRows = rows.filter(r => ['subjects_heading', 'subjects_subhead'].includes(r.key));
  const introKeys: Array<{ key: string; label: string; eyebrow: string }> = [
    { key: 'subjects_11plus_intro', label: '11+ intro', eyebrow: '11+' },
    { key: 'subjects_gcse_intro', label: 'GCSE intro', eyebrow: 'GCSE' },
    { key: 'subjects_alevel_intro', label: 'A-level intro', eyebrow: 'A-level' },
    { key: 'subjects_oxbridge_intro', label: 'Oxbridge intro', eyebrow: 'Oxbridge' },
  ];

  return (
    <>
      <SectionCard
        title="Subjects — Page heading"
        caption="The h1 and sub-head at the top of the Subjects page."
        rows={headingRows}
        edits={edits}
        onChange={onChange}
        onSave={onSave}
        saving={anySaving(headingRows.map(r => r.key), savingKeys)}
      >
        {() => (
          <>
            <Field
              fieldKey="subjects_heading"
              row={headingRows.find(r => r.key === 'subjects_heading')}
              edit={edits.subjects_heading}
              onChange={onChange}
              previewClass="font-serif text-[clamp(2rem,4.5vw,3.5rem)] leading-[1.05] tracking-tight text-ink"
            />
            <div className="mt-4 max-w-2xl">
              <Field
                fieldKey="subjects_subhead"
                row={headingRows.find(r => r.key === 'subjects_subhead')}
                edit={edits.subjects_subhead}
                onChange={onChange}
                previewClass="font-sans text-[17px] text-muted leading-[1.55]"
                multiline
                rows={2}
              />
            </div>
          </>
        )}
      </SectionCard>

      {introKeys.map(({ key, label, eyebrow }) => {
        const thisRows = rows.filter(r => r.key === key);
        return (
          <SectionCard
            key={key}
            title={`Subjects — ${label}`}
            caption="Intro paragraph that sits above the subjects list for this level."
            rows={thisRows}
            edits={edits}
            onChange={onChange}
            onSave={onSave}
            saving={anySaving([key], savingKeys)}
          >
            {() => (
              <>
                <p className="eyebrow mb-3">{eyebrow}</p>
                <div className="max-w-2xl">
                  <Field
                    fieldKey={key}
                    row={thisRows[0]}
                    edit={edits[key]}
                    onChange={onChange}
                    previewClass="font-sans text-[17px] text-muted leading-[1.55]"
                    multiline
                    rows={3}
                  />
                </div>
              </>
            )}
          </SectionCard>
        );
      })}

      {(() => {
        const packageRows = rows.filter(r => ['packages_heading', 'packages_body'].includes(r.key));
        if (packageRows.length === 0) return null;
        return (
          <SectionCard
            title="Subjects — Hour packages block"
            caption="Heading and body that introduce the prepaid packages list. The discount percentages themselves are managed in Financials."
            rows={packageRows}
            edits={edits}
            onChange={onChange}
            onSave={onSave}
            saving={anySaving(packageRows.map(r => r.key), savingKeys)}
          >
            {() => (
              <>
                <Field
                  fieldKey="packages_heading"
                  row={packageRows.find(r => r.key === 'packages_heading')}
                  edit={edits.packages_heading}
                  onChange={onChange}
                  previewClass="font-serif text-[clamp(1.75rem,3.5vw,2.5rem)] leading-[1.1] text-ink"
                />
                <div className="mt-4 max-w-2xl">
                  <Field
                    fieldKey="packages_body"
                    row={packageRows.find(r => r.key === 'packages_body')}
                    edit={edits.packages_body}
                    onChange={onChange}
                    previewClass="font-sans text-[17px] text-muted leading-[1.55]"
                    multiline
                    rows={3}
                  />
                </div>
              </>
            )}
          </SectionCard>
        );
      })()}

      {(() => {
        const noteKeys = ['rates_note_1', 'rates_note_2', 'rates_note_3'];
        const noteRows = rows.filter(r => noteKeys.includes(r.key));
        if (noteRows.length === 0) return null;
        return (
          <SectionCard
            title="Subjects — Notes"
            caption="The three small-print notes below the rate tables."
            rows={noteRows}
            edits={edits}
            onChange={onChange}
            onSave={onSave}
            saving={anySaving(noteKeys, savingKeys)}
          >
            {() => (
              <ul className="max-w-2xl">
                {noteKeys.map(k => (
                  <li key={k} className="border-t border-rule py-3 last:border-b">
                    <Field
                      fieldKey={k}
                      row={noteRows.find(r => r.key === k)}
                      edit={edits[k]}
                      onChange={onChange}
                      previewClass="font-sans text-[17px] text-ink leading-[1.55]"
                    />
                  </li>
                ))}
              </ul>
            )}
          </SectionCard>
        );
      })()}
    </>
  );
};

const OxbridgePreview: React.FC<PreviewProps> = ({ rows, edits, onChange, onSave, savingKeys }) => {
  const headingRows = rows.filter(r => ['oxbridge_heading', 'oxbridge_intro'].includes(r.key));
  const blocks: Array<{ h: string; b: string; eyebrow: string; title: string }> = [
    { h: 'oxbridge_tara_h', b: 'oxbridge_tara_body', eyebrow: 'Admissions test', title: 'TARA block' },
    { h: 'oxbridge_interview_h', b: 'oxbridge_interview_body', eyebrow: 'Interviews', title: 'Interview block' },
    { h: 'oxbridge_personal_h', b: 'oxbridge_personal_body', eyebrow: 'Written work', title: 'Personal statement block' },
  ];

  return (
    <>
      <SectionCard
        title="Oxbridge — Page heading"
        caption="Headline and intro paragraph."
        rows={headingRows}
        edits={edits}
        onChange={onChange}
        onSave={onSave}
        saving={anySaving(headingRows.map(r => r.key), savingKeys)}
      >
        {() => (
          <>
            <Field
              fieldKey="oxbridge_heading"
              row={headingRows.find(r => r.key === 'oxbridge_heading')}
              edit={edits.oxbridge_heading}
              onChange={onChange}
              previewClass="font-serif text-[clamp(2rem,4.5vw,3.5rem)] leading-[1.05] tracking-tight text-ink"
              multiline
              rows={2}
            />
            <div className="mt-5 max-w-2xl">
              <Field
                fieldKey="oxbridge_intro"
                row={headingRows.find(r => r.key === 'oxbridge_intro')}
                edit={edits.oxbridge_intro}
                onChange={onChange}
                previewClass="font-sans text-[17px] leading-[1.55] text-ink"
                multiline
                rows={4}
              />
            </div>
          </>
        )}
      </SectionCard>

      {blocks.map(({ h, b, eyebrow, title }) => {
        const blockRows = rows.filter(r => r.key === h || r.key === b);
        return (
          <SectionCard
            key={h}
            title={`Oxbridge — ${title}`}
            rows={blockRows}
            edits={edits}
            onChange={onChange}
            onSave={onSave}
            saving={anySaving([h, b], savingKeys)}
          >
            {() => (
              <>
                <p className="eyebrow mb-3">{eyebrow}</p>
                <Field
                  fieldKey={h}
                  row={blockRows.find(r => r.key === h)}
                  edit={edits[h]}
                  onChange={onChange}
                  previewClass="font-serif text-[clamp(1.75rem,3.5vw,2.5rem)] leading-[1.1] text-ink"
                />
                <div className="mt-4 max-w-2xl">
                  <Field
                    fieldKey={b}
                    row={blockRows.find(r => r.key === b)}
                    edit={edits[b]}
                    onChange={onChange}
                    previewClass="font-sans text-[17px] leading-[1.55] text-ink"
                    multiline
                    rows={4}
                  />
                </div>
              </>
            )}
          </SectionCard>
        );
      })}
    </>
  );
};

const ContactPreview: React.FC<PreviewProps> = ({ rows, edits, onChange, onSave, savingKeys }) => {
  const allKeys = ['contact_heading', 'contact_subhead', 'contact_thanks'];
  return (
    <SectionCard
      title="Contact — Page copy"
      caption="Heading, sub-head, and the message shown after someone submits the form."
      rows={rows}
      edits={edits}
      onChange={onChange}
      onSave={onSave}
      saving={anySaving(allKeys, savingKeys)}
    >
      {() => (
        <>
          <Field
            fieldKey="contact_heading"
            row={rows.find(r => r.key === 'contact_heading')}
            edit={edits.contact_heading}
            onChange={onChange}
            previewClass="font-serif text-[clamp(2rem,4.5vw,3.5rem)] leading-[1.05] tracking-tight text-ink"
          />
          <div className="mt-4 max-w-2xl">
            <Field
              fieldKey="contact_subhead"
              row={rows.find(r => r.key === 'contact_subhead')}
              edit={edits.contact_subhead}
              onChange={onChange}
              previewClass="font-sans text-[17px] text-muted leading-[1.55]"
              multiline
              rows={2}
            />
          </div>
          <div className="mt-8 border-t border-rule pt-6">
            <p className="eyebrow mb-3">Thank-you screen</p>
            <div className="max-w-2xl">
              <Field
                fieldKey="contact_thanks"
                row={rows.find(r => r.key === 'contact_thanks')}
                edit={edits.contact_thanks}
                onChange={onChange}
                previewClass="font-sans text-[17px] text-ink leading-[1.55]"
                multiline
                rows={2}
              />
            </div>
          </div>
        </>
      )}
    </SectionCard>
  );
};

const FooterPreview: React.FC<PreviewProps> = ({ rows, edits, onChange, onSave, savingKeys }) => {
  const allKeys = ['footer_brand', 'footer_blurb', 'footer_hours_h', 'footer_hours_body', 'footer_safeguarding'];
  return (
    <SectionCard
      title="Footer"
      caption="Site-wide footer. Brand wordmark, blurb, availability heading/body, and safeguarding line."
      rows={rows}
      edits={edits}
      onChange={onChange}
      onSave={onSave}
      saving={anySaving(allKeys, savingKeys)}
    >
      {() => (
        <div className="bg-navy text-cream rounded-sm p-6 md:p-8 grid md:grid-cols-3 gap-8">
          <div>
            <div className="text-cream font-serif text-[19px]">
              <Field
                fieldKey="footer_brand"
                row={rows.find(r => r.key === 'footer_brand')}
                edit={edits.footer_brand}
                onChange={onChange}
                previewClass="font-serif text-[19px] text-cream"
              />
            </div>
            <div className="mt-4">
              <Field
                fieldKey="footer_blurb"
                row={rows.find(r => r.key === 'footer_blurb')}
                edit={edits.footer_blurb}
                onChange={onChange}
                previewClass="font-sans text-[15px] text-cream/80 leading-relaxed"
                multiline
                rows={4}
              />
            </div>
          </div>
          <div className="text-cream/70 text-[13px]">
            <p className="font-serif text-[18px] text-cream mb-3">Pages</p>
            <p>(static nav links — not editable)</p>
          </div>
          <div>
            <Field
              fieldKey="footer_hours_h"
              row={rows.find(r => r.key === 'footer_hours_h')}
              edit={edits.footer_hours_h}
              onChange={onChange}
              previewClass="font-serif text-[18px] text-cream"
            />
            <div className="mt-3">
              <Field
                fieldKey="footer_hours_body"
                row={rows.find(r => r.key === 'footer_hours_body')}
                edit={edits.footer_hours_body}
                onChange={onChange}
                previewClass="font-sans text-[15px] text-cream/80 leading-relaxed"
                multiline
                rows={3}
              />
            </div>
            <div className="mt-4">
              <Field
                fieldKey="footer_safeguarding"
                row={rows.find(r => r.key === 'footer_safeguarding')}
                edit={edits.footer_safeguarding}
                onChange={onChange}
                previewClass="font-sans text-[13px] text-cream/60"
                multiline
                rows={2}
              />
            </div>
          </div>
        </div>
      )}
    </SectionCard>
  );
};

export default PagePreviewEditor;
